import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import ChatWidget from '../components/ChatWidget';
import InlineSignupForm from '../components/InlineSignupForm';
import { captureAttribution } from '../utils/attribution';
import { initFbPixel } from '../utils/fbPixel';
import { API_BASE_URL } from '../config';
import './StartLanding.css';

// Module-level so a remount (or StrictMode's double-invoked effect in dev)
// can't post the same page view twice and inflate the funnel denominator.
let _lpViewSent = false;

/**
 * /start — dedicated Facebook ad landing page.
 *
 * Home2 stays untouched. All chat-first onboarding traffic lands here.
 * Layout is hero + trust signals + embedded chat widget + inline signup
 * form. Attribution (UTM, fbclid, _fbp, _fbc, referrer, landing_url) is
 * captured on mount, stored first-touch in sessionStorage, and flows through
 * to both the web-chat session and the signup POST.
 */
export default function StartLanding() {
  const [sessionId, setSessionId] = useState(null);
  const [signupFocused, setSignupFocused] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const signupRef = useRef(null);

  // On mount: capture attribution, init FB Pixel, record the lp_view funnel
  // event. initFbPixel() is a no-op when the index.html <head> snippet already
  // booted the pixel — it stays here so the LP still works if that is skipped.
  useEffect(() => {
    const attribution = captureAttribution();
    initFbPixel();

    if (_lpViewSent) return;
    _lpViewSent = true;
    // Fire-and-forget: analytics must never block or break the LP.
    axios
      .post(`${API_BASE_URL}/api/v1/web-chat/lp-view`, { attribution })
      .catch(() => {});
  }, []);

  // Sticky mobile CTA: only after the user scrolls past the hero (~600px).
  // Hidden once they reach the inline signup form to avoid double-CTA noise.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const formTop = signupRef.current
        ? signupRef.current.getBoundingClientRect().top + window.scrollY
        : Infinity;
      setShowStickyCta(y > 600 && y + window.innerHeight < formTop + 120);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // When the chat widget hits its exchange cap, scroll to the signup form.
  const handleSignupNudge = () => {
    setSignupFocused(true);
    if (signupRef.current) {
      signupRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box className="start-lp">
      {/* ---- HERO ---- */}
      {/* No standalone brand bar: an unknown logo costs premium viewport and
          confers no trust. Corporate identity lives in the chat header
          ("Aven · by Beck Health") where it does legitimacy work for free. */}
      <Box className="start-hero">
        <Container maxWidth="lg">
          {/* Three ordered blocks. Mobile (xs): title → chat → trust/CTA,
              so a real headline sets context before the conversation. Desktop
              (md): title-left, chat-right, trust/CTA wraps under the title. */}
          <Grid container spacing={4} alignItems="flex-start">
            {/* A — Title block */}
            <Grid item xs={12} md={6} order={{ xs: 1, md: 1 }}>
              {/* Problem in the title, solution in the sub — one thought,
                  finished across two lines. No eyebrow: two levels beat three
                  in the 3-second read. */}
              <Typography variant="h2" component="h1" className="start-hero-headline">
                The hardest part of a GLP-1 isn&rsquo;t the shot.
              </Typography>
              <Typography variant="h6" component="p" className="start-hero-sub">
                It&rsquo;s every day in between. Aven is a coach in your pocket,
                checking in by text so you&rsquo;re never doing it alone.
              </Typography>
            </Grid>

            {/* B — Chat */}
            <Grid item xs={12} md={6} order={{ xs: 2, md: 2 }}>
              <Box id="chat-anchor">
                <ChatWidget
                  onSignupNudge={handleSignupNudge}
                  onSessionStart={setSessionId}
                />
              </Box>
            </Grid>

            {/* C — Trust signals + disclaimer + CTA */}
            <Grid item xs={12} md={6} order={{ xs: 3, md: 3 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box className="trust-signal">
                  <LockOutlinedIcon fontSize="small" />
                  <span>Private by design</span>
                </Box>
                <Box className="trust-signal">
                  <SmsOutlinedIcon fontSize="small" />
                  <span>No app. Just text.</span>
                </Box>
                <Box className="trust-signal">
                  <CheckCircleOutlineIcon fontSize="small" />
                  <span>Always available</span>
                </Box>
              </Stack>

              <Typography variant="caption" sx={{ display: 'block', mt: 2.5, color: 'text.secondary' }}>
                Built for people using GLP-1 medications like Ozempic, Wegovy, Mounjaro,
                and Zepbound. Aven is not affiliated with or endorsed by any pharmaceutical
                company or drug manufacturer.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ---- VALUE PROPS ---- */}
      <Box className="start-value">
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {[
              {
                num: '01',
                title: 'Support that remembers you',
                body: 'Aven remembers your goals, struggles, side effects, wins, and routines — so support feels personal, not generic.',
              },
              {
                num: '02',
                title: 'No app. Just text.',
                body: 'No dashboards, no forms, no app to remember. Aven checks in through simple texts that fit into your day.',
              },
              {
                num: '03',
                title: 'Your doctor handles your care. Aven helps with the day-to-day.',
                body: 'Aven does not diagnose, prescribe, or change your medication. We help with the habits, food choices, motivation, and consistency that happen between appointments.',
              },
            ].map((c) => (
              <Grid item xs={12} md={4} key={c.title}>
                <Box className="start-value-block">
                  <Typography component="span" className="start-value-num">{c.num}</Typography>
                  <Typography component="h3" className="start-value-title">{c.title}</Typography>
                  <Typography component="p" className="start-value-body">{c.body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ---- REPEAT CTA + PRICE CLARITY ---- */}
      <Box className="start-midcta">
        <Container maxWidth="sm">
          <Typography component="h2" className="start-midcta-headline">
            Free to start.
          </Typography>
          <Typography component="p" className="start-midcta-body">
            Try Aven free for a week of texts. If it's clicking, plans are
            <strong> $20/mo</strong> — we'll set that up later. Cancel anytime.
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="primary"
            className="start-cta"
            disableElevation
            onClick={() => {
              const el = document.getElementById('chat-anchor');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Talk to Aven now
          </Button>
        </Container>
      </Box>

      {/* ---- WHAT AVEN FEELS LIKE ---- */}
      <Box className="start-proof">
        <Container maxWidth="md">
          <Box className="start-quote">
            <Typography component="span" className="start-quote-eyebrow">
              What Aven is designed to feel like
            </Typography>
            <Typography component="p" className="start-quote-text">
              Like someone who actually remembers what you're working on — your
              goals, your struggles, the wins along the way — and helps you keep
              going between doctor visits.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ---- INLINE SIGNUP ---- */}
      <Box ref={signupRef} className={`start-signup ${signupFocused ? 'is-focused' : ''}`}>
        <Container maxWidth="sm">
          <Typography variant="h4" component="h2" sx={{ mb: 1, textAlign: 'center' }}>
            Let&rsquo;s take this to your phone
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
            Texting is where Aven does its best coaching &mdash; check-ins that
            find you in your day, no app to open. Aven remembers this chat and
            picks up right where you left off. Free to start, no credit card,
            no commitment.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 3, textAlign: 'center', display: 'block' }}>
            Takes about 30 seconds.
          </Typography>
          <InlineSignupForm sessionId={sessionId} />
        </Container>
      </Box>

      {/* ---- STICKY MOBILE CTA ---- */}
      <Box className={`start-sticky-cta ${showStickyCta ? 'is-visible' : ''}`} aria-hidden={!showStickyCta}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="start-cta"
          disableElevation
          onClick={() => {
            const el = document.getElementById('chat-anchor');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Talk to Aven
        </Button>
      </Box>

      {/* ---- FOOTER ---- */}
      <Box className="start-footer">
        <Container maxWidth="lg">
          <Typography variant="caption" color="text.secondary">
            Aven is not a substitute for medical care. We do not provide dosing,
            diagnosis, or treatment advice. For emergencies, call 911 or 988.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <a href="/privacy">Privacy</a> &nbsp;·&nbsp;
            <a href="/terms">Terms</a>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
